// React 18 exported `createRoot` from both `react-dom` and `react-dom/client`;
// React 19 exports it only from `react-dom/client`. antd 4.23.5 — vendored
// inside syngenta-digital-cropwise-react-ui-kit's prebuilt bundle — feature
// detects like this:
//
//   L = {...ReactDOM}; k = L.render;
//   try { Number(L.version.split('.')[0]) >= 18 && (w = L.createRoot) } catch {}
//
// Under React 19 that leaves BOTH `w` (createRoot) and `k` (legacy render)
// undefined, so every static API — Modal.confirm, Modal.warning, message.*,
// notification.* — silently does nothing. It fails inside an
// asyncToGenerator wrapper, so it surfaces as an unhandled rejection rather
// than a console error, which is why the symptom is a dead button rather than
// a crash. Concretely: the SelectPropertiesDrawerV2 Cancel button routes
// through showConfirmModal when there are unsaved changes and never opens.
//
// Re-adding createRoot/hydrateRoot to the `react-dom` namespace makes antd 4's
// own React 18+ branch activate, which is the path it already uses correctly.
//
// This module deliberately does NOT import 'react-dom/client': react's own
// cjs/react-dom-client.development.js eagerly does `require("react-dom")`,
// which the alias points back here, and it would read this module's exports
// while they are still in the temporal dead zone. Instead the real
// implementations are published onto globalThis by src/index.jsx and read on
// first *call* — antd only needs `createRoot` to be a truthy function at
// module-init time; it isn't invoked until a modal actually opens.
//
// Remove once the ui-kit ships a build on antd 5 (or React 19-aware antd 4).
// eslint-disable-next-line import/no-unresolved -- vite.config.js alias
import * as ReactDOM from '__react_dom_actual__';

export const REACT_DOM_CLIENT_GLOBAL = '__CW_REACT_DOM_CLIENT__';

function client() {
  const ns = globalThis[REACT_DOM_CLIENT_GLOBAL];
  if (!ns) {
    throw new Error(
      `react-dom/client was read before src/index.jsx published it on globalThis.${REACT_DOM_CLIENT_GLOBAL} (see vite-shims/react-dom-legacy-compat.js)`
    );
  }
  return ns;
}

export function createRoot(container, options) {
  return client().createRoot(container, options);
}

export function hydrateRoot(container, initialChildren, options) {
  return client().hydrateRoot(container, initialChildren, options);
}

// React 19 also removed findDOMNode. Two prebuilt bundles still call it:
//   - rc-trigger's getRootDomNode (vendored antd 4 in the ui-kit) — its popup
//     positioning falls back to findDOMNode whenever the trigger ref isn't
//     already an element, which is what breaks Save in the property drawer.
//   - grow-ui's inlined useIsFocusVisible, which calls it unguarded on mount.
// Both only ever want the host element for a component, which is recoverable
// by walking the fiber tree that React still exposes on class instances.
// Depth-first, matching the tree order React's own findCurrentHostFiber
// returns. Following only the `.child` chain would miss a host element living
// in a later branch (`<><Foo /><div /></>` returns null once Foo renders no
// host node) — which is precisely the shape rc-trigger positions against.
// Siblings of `fiber` itself are excluded: the search is scoped to its subtree.
function findHostElement(fiber) {
  if (fiber.stateNode && fiber.stateNode.nodeType === 1) return fiber.stateNode;
  const stack = fiber.child ? [fiber.child] : [];
  while (stack.length) {
    const node = stack.pop();
    if (node.stateNode && node.stateNode.nodeType === 1) return node.stateNode;
    // Sibling first, child second: pop() is LIFO, so the child branch is
    // explored before moving sideways.
    if (node.sibling) stack.push(node.sibling);
    if (node.child) stack.push(node.child);
  }
  return null;
}

export function findDOMNode(instanceOrElement) {
  if (instanceOrElement == null) return null;
  // Already a DOM node (element or text) — the common case, and the one both
  // call sites hit most.
  if (typeof instanceOrElement.nodeType === 'number') return instanceOrElement;
  const fiber =
    instanceOrElement._reactInternals || instanceOrElement._reactInternalFiber;
  if (!fiber) return null;
  // React double-buffers fibers, and `_reactInternals` can point at the one
  // whose child chain isn't the committed one — react-transition-group calls
  // this from componentDidUpdate, mid-commit, and hits exactly that. The real
  // findDOMNode resolved it with findCurrentFiberUsingSlowPath; checking the
  // alternate covers the same case without reaching into React internals.
  return (
    findHostElement(fiber) ??
    (fiber.alternate ? findHostElement(fiber.alternate) : null)
  );
}

/* eslint-disable camelcase -- mirroring react-dom's own export names verbatim */
export const {
  __DOM_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE,
  createPortal,
  flushSync,
  preconnect,
  prefetchDNS,
  preinit,
  preinitModule,
  preload,
  preloadModule,
  requestFormReset,
  unstable_batchedUpdates,
  useFormState,
  useFormStatus,
  version
} = ReactDOM;
/* eslint-enable camelcase */

// `import ReactDOM from 'react-dom'` and the CJS interop path both read the
// default, so it has to carry the re-added APIs too — rc-trigger reaches them
// exactly this way (`react_dom_legacy_compat_default.findDOMNode`).
export default { ...ReactDOM, createRoot, hydrateRoot, findDOMNode };
